import React, { useState, useEffect } from "react";
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Typography, 
  Menu, 
  MenuItem, 
  Avatar, 
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Badge,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Tooltip,
  useTheme,
  Paper,
  Container,
  Fade,
  Chip,
  alpha,
  Button,
  CircularProgress
} from "@mui/material";
import { 
  Dashboard,
  PeopleAlt, 
  ExitToApp, 
  Menu as MenuIcon,
  Person,
  Notifications,
  Settings,
  Inventory,
  LocalShipping,
  Payments,
  BarChart,
  Category,
  AdminPanelSettings,
  Flag,
  Storefront,
  Security,
  ReportProblem,
  SupervisorAccount,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  Close,
  Search,
  ArrowDropDown,
  Brightness4,
  Brightness7,
  NotificationsNone,
  HelpOutline,
  ImageOutlined,
  Help,
  Message as MessageIcon,
  Forum
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { blue, grey } from "@mui/material/colors";
import pb from "./pocketbase";

// Styled components with enhanced styles for a more modern look
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : theme.palette.background.paper,
  color: theme.palette.text.primary,
  backdropFilter: 'blur(10px)',
  boxShadow: 'none',
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  zIndex: theme.zIndex.drawer + 1,
}));

const MenuButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(1),
  color: theme.palette.text.primary,
  borderRadius: 8,
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s',
  width: 40,
  height: 40,
  backgroundColor: theme.palette.primary.main,
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
}));

const NavItem = styled(Box)(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0.7, 1.5),
  marginRight: theme.spacing(0.5),
  height: 42,
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  color: active ? theme.palette.primary.main : theme.palette.text.primary,
  backgroundColor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
  fontWeight: active ? 600 : 400,
  '&:hover': {
    backgroundColor: active ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.action.hover, 0.7),
  }
}));

const NotificationBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    fontSize: 10,
    height: 18,
    minWidth: 18,
    padding: '0 5px',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
  },
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2.5, 2),
  ...theme.mixins.toolbar,
}));

const DrawerFooter = styled(Box)(({ theme }) => ({
  position: 'sticky',
  bottom: 0,
  background: theme.palette.background.paper,
  boxShadow: `0 -1px 5px rgba(0,0,0,0.05)`,
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
}));

const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: 8,
  margin: theme.spacing(0.3, 0.8),
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: alpha(theme.palette.action.hover, 0.7),
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  overflow: 'hidden',
}));

const Navbar = ({ toggleDarkMode, isDarkMode }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isXS = useMediaQuery(theme.breakpoints.down('sm'));
  const isMD = useMediaQuery(theme.breakpoints.down('md'));
  const isLG = useMediaQuery(theme.breakpoints.down('xl'));
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [appInfo, setAppInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [helpAnchorEl, setHelpAnchorEl] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);

  // Format current date - using fixed date from user input
  const formattedDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'Asia/Dhaka' // GMT+6

  });

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        setLoading(true);
        
        // Check if user is authenticated
        if (pb.authStore.isValid) {
          try {
            const rawadminData = await pb.collection("admin").getOne(pb.authStore.model.id);
            setAdmin(rawadminData);
            
            // Fetch app info
            const appInfoData = await pb.collection("appinfo").getList(1, 1);
            if (appInfoData.items.length > 0) {
              setAppInfo(appInfoData.items[0]);
            } else {
              // Fallback app info
              setAppInfo({
                appName: "E-Commerce Admin",
                version: "1.0.0"
              });
            }
            
            // Simulate notifications (in a real app, you'd fetch these from an API)
            const mockNotifications = [
              { 
                id: 1, 
                type: 'alert', 
                message: 'New seller registration needs approval', 
                time: '5 minutes ago',
                read: false
              },
              { 
                id: 2, 
                type: 'warning', 
                message: '3 products flagged for review', 
                time: '1 hour ago',
                read: false
              },
              { 
                id: 3, 
                type: 'info', 
                message: 'Monthly sales report is ready', 
                time: '2 hours ago',
                read: true
              },
              { 
                id: 4, 
                type: 'success', 
                message: 'System update completed successfully', 
                time: '1 day ago',
                read: true
              },
            ];
            setNotifications(mockNotifications);
            setUnreadCount(mockNotifications.filter(n => !n.read).length);
          } catch (error) {
            console.error("Error fetching admin data:", error);
            
            // MODIFIED: Only clear auth and redirect for actual auth errors (401/403)
            // Not for other errors like missing collections
            if (error.status === 401 || error.status === 403) {
              pb.authStore.clear();
              navigate('/login');
            }
          }
        } else {
          // Not authenticated, redirect to login
          navigate('/login');
        }
      } catch (error) {
        console.error("Authentication error:", error);
        navigate('/login');
      } finally {
        setLoading(false);
      }

      // Fetch unread support messages count
      try {
        const result = await pb.collection('admin_chat').getList(1, 500, {
          filter: 'sender = "user" && seen = false',
          sort: '-created',
        });
        
        const unreadCount = result.totalItems;
        setUnreadMessages(unreadCount);
      } catch (error) {
        console.error("Error fetching unread messages:", error);
        setUnreadMessages(0);
      }
    };
    
    fetchAdminData();
    
    // Set up polling for unread messages
    const interval = setInterval(async () => {
      if (pb.authStore.isValid) {
        try {
          // Changed from admin_chat_conversations to admin_chat
          const unreadMessagesData = await pb.collection('admin_chat').getList(1, 50, {
            // Remove the filter that's causing the error
            // filter: 'unread_count > 0',
            sort: '-created',
          });
          
          // Since we can't filter by unread_count on the server side,
          // we'll need to check it on the client side if the field exists
          const totalUnread = unreadMessagesData.items.reduce((sum, conv) => {
            // Check if the field exists before trying to use it
            return sum + (conv.unread_count || 0);
          }, 0);
          
          setUnreadMessages(totalUnread);
        } catch (error) {
          console.error("Error polling unread messages:", error);
          // Don't change unread count on error
        }
      }
    }, 30000); // Check every 30 seconds
    
    return () => clearInterval(interval);
  }, [navigate]);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  
  const handleNotificationOpen = (event) => setNotificationAnchorEl(event.currentTarget);
  const handleNotificationClose = () => setNotificationAnchorEl(null);
  
  const handleHelpOpen = (event) => setHelpAnchorEl(event.currentTarget);
  const handleHelpClose = () => setHelpAnchorEl(null);
  
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  const handleLogout = () => {
    pb.authStore.clear();
    navigate("/login");
  };

  // Admin menu items
  const adminMenuItems = [
    { label: "Dashboard", icon: <Dashboard />, path: "/dashboard" },
    { label: "Sellers", icon: <Storefront />, path: "/sellers" },
    { label: "Products", icon: <Inventory />, path: "/products" },
    { label: "Orders", icon: <LocalShipping />, path: "/orders" },
    { label: "Customers", icon: <PeopleAlt />, path: "/customers" },
    { label: "Categories", icon: <Category />, path: "/categories" },
    { label: "Payments", icon: <Payments />, path: "/payments" },
    { label: "Banners", icon: <ImageOutlined />, path: "/banners" },
    { label:"Q&A", icon: <Help />, path: "/qna" },
    { label: "Support Chat", icon: <MessageIcon />, path: "/support-messages" },
    { label: "Seller Chat", icon: <Forum />, path: "/seller-chat" },
    { label: "Settings", icon: <Settings />, path: "/settings" },
  ];

  // Helper to check if a path is active
  const isActive = (path) => location.pathname === path;

  // If still loading or not authenticated, render nothing or a small loading indicator
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 64,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`
      }}>
        <CircularProgress size={28} color="primary" />
      </Box>
    );
  }

  if (!pb.authStore.isValid) {
    return null;
  }

  // Admin avatar (fallback to first letter of name)
  const adminInitial = admin?.name 
    ? admin.name.charAt(0).toUpperCase() 
    : admin?.username 
      ? admin.username.charAt(0).toUpperCase() 
      : "A";
  
  // Show full/compact navigation based on screen size
  const showFullNavigation = !isLG;
  
  // App logo (fallback to AdminPanelSettings icon)
  const hasLogo = appInfo?.logo && typeof pb.files.getUrl === 'function';
  const logoUrl = hasLogo ? pb.files.getUrl(appInfo, appInfo.logo) : null;
  
  // Helper function to render notification icon based on type
  const getNotificationIcon = (type) => {
    switch(type) {
      case 'alert': return <Flag fontSize="small" color="error" />;
      case 'warning': return <ReportProblem fontSize="small" color="warning" />;
      case 'info': return <Notifications fontSize="small" color="info" />;
      case 'success': return <CheckCircle fontSize="small" color="success" />;
      default: return <Notifications fontSize="small" color="action" />;
    }
  };

  const currentUser = admin?.name || admin?.username || "Admin";
  
  return (
    <StyledAppBar position="sticky">
      <Container maxWidth="2xl" disableGutters>
        <Toolbar sx={{ gap: 2, minHeight: { xs: 64, sm: 70 }, px: { xs: 1, sm: 2, md: 3 } }}>
          {/* Mobile Menu Button */}
          {isLG && (
            <MenuButton 
              onClick={toggleMobileMenu} 
              size="medium"
              aria-label="Open menu"
            >
              <MenuIcon />
            </MenuButton>
          )}

          {/* Brand Section */}
          <Box 
            onClick={() => navigate("/dashboard")} 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5, 
              mr: 3,
              cursor: 'pointer',
              flexGrow: isXS ? 1 : 0
            }}
          >
            <Avatar 
              src={logoUrl} 
              sx={{ 
                width: { xs: 38, sm: 42 }, 
                height: { xs: 38, sm: 42 },
                bgcolor: 'white',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'scale(1.05)' }
              }}
              variant="rounded"
            >
              <AdminPanelSettings />
            </Avatar>
            
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  letterSpacing: -0.5,
                  lineHeight: 1.2,
                }}
              >
                {appInfo?.appName || "E-Commerce Admin"}
              </Typography>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  display: 'block',
                  fontSize: '0.7rem'
                }}
              >
                {formattedDate}
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation */}
          {showFullNavigation && (
            <Box sx={{ 
              display: 'flex', 
              gap: 0.5, 
              flexGrow: 1,
              overflowX: 'auto',
              py: 1,
              '&::-webkit-scrollbar': {
                height: 0,
              }
            }}>
              {adminMenuItems.map((item) => (
                <NavItem 
                  key={item.label}
                  onClick={() => navigate(item.path)} 
                  active={isActive(item.path)}
                >
                  {item.icon}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      ml: 1,
                      whiteSpace: 'nowrap',
                      fontWeight: isActive(item.path) ? 600 : 400
                    }}
                  >
                    {item.label}
                  </Typography>
                </NavItem>
              ))}
            </Box>
          )}

          {/* Right Section - Controls */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 0.5, sm: 1.5 },
            ml: 'auto'
          }}>
            {/* Theme Toggle */}
            <Tooltip title="Support Messages">
              <IconButton 
                onClick={() => navigate('/support-messages')}
                sx={{ 
                  borderRadius: 1.5,
                  color: theme.palette.text.secondary
                }}
              >
                <NotificationBadge badgeContent={unreadMessages} color="error">
                  <MessageIcon fontSize="small" />
                </NotificationBadge>
              </IconButton>
            </Tooltip>
            
            {/* <Tooltip title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
              <IconButton 
                onClick={toggleDarkMode} 
                color="inherit"
                sx={{ 
                  borderRadius: 1.5,
                  color: theme.palette.text.secondary
                }}
              >
                {isDarkMode ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
              </IconButton>
            </Tooltip>
            
             <Tooltip title="Help & Resources">
              <IconButton 
                sx={{ 
                  color: theme.palette.text.secondary,
                  borderRadius: 1.5,
                  display: { xs: 'none', sm: 'flex' }
                }} 
                onClick={handleHelpOpen}
              >
                <HelpOutline fontSize="small" />
              </IconButton>
            </Tooltip>
            
             <Tooltip title="Notifications">
              <IconButton 
                sx={{ 
                  color: theme.palette.text.secondary,
                  borderRadius: 1.5
                }} 
                onClick={handleNotificationOpen}
              >
                <NotificationBadge badgeContent={unreadCount} color="error">
                  <NotificationsNone fontSize="small" />
                </NotificationBadge>
              </IconButton>
            </Tooltip> */}
            
            {/* Admin User Display - Only on larger screens */}
            {!isXS && (
              <Box 
                onClick={handleMenuOpen}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  p: 0.5,
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.action.hover, 0.7)
                  }
                }}
              >
                <UserAvatar>
                  {adminInitial}
                </UserAvatar>
                <Box sx={{ 
                  ml: 1.5, 
                  mr: 0.5,
                  display: { xs: 'none', md: 'block' }
                }}>
                  <Typography 
                    variant="body2" 
                    fontWeight={600}
                    sx={{ lineHeight: 1.2 }}
                  >
                    {currentUser}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    Administrator
                  </Typography>
                </Box>
                <ArrowDropDown 
                  fontSize="small" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    display: { xs: 'none', md: 'block' }
                  }} 
                />
              </Box>
            )}
            
            {/* Mobile User Avatar - Only on extra small screens */}
            {isXS && (
              <IconButton onClick={handleMenuOpen} sx={{ p: 0.3 }}>
                <UserAvatar sx={{ width: 36, height: 36 }}>
                  {adminInitial}
                </UserAvatar>
              </IconButton>
            )}
          </Box>

          {/* Mobile Menu Drawer */}
          <Drawer
            anchor="left"
            open={mobileMenuOpen}
            onClose={toggleMobileMenu}
            PaperProps={{
              sx: {
                width: { xs: '85%', sm: 300 },
                borderRadius: '0 16px 16px 0',
                boxSizing: 'border-box',
                border: 'none',
              }
            }}
          >
            <DrawerHeader>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar 
                  src={logoUrl} 
                  sx={{ 
                    width: 42, 
                    height: 42,
                    bgcolor: blue[600]
                  }}
                  variant="rounded"
                >
                  <AdminPanelSettings />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {appInfo?.appName || "E-Commerce Admin"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Version {appInfo?.version || "1.0.0"}
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={toggleMobileMenu} size="small">
                <Close />
              </IconButton>
            </DrawerHeader>
            
            <Divider sx={{ opacity: 0.6 }} />
            
            {/* Admin Info in Drawer */}
            <Box sx={{ p: 2, mt: 1 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                mb: 2
              }}>
                <UserAvatar>
                  {adminInitial}
                </UserAvatar>
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    {currentUser}
                  </Typography>
                  <Chip 
                    label="Administrator" 
                    size="small" 
                    color="primary" 
                    sx={{ 
                      height: 22, 
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      color: 'white',
                      mt: 0.5
                    }} 
                  />
                </Box>
              </Box>
            </Box>
            
            <Divider sx={{ opacity: 0.6 }} />
            
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography 
                variant="overline" 
                color="text.secondary" 
                fontWeight={700}
                sx={{ letterSpacing: 1 }}
              >
                MAIN MENU
              </Typography>
            </Box>
            
            <List sx={{ px: 1.5 }}>
              {adminMenuItems.map((item) => (
                <ListItem key={item.label} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton 
                    onClick={() => { navigate(item.path); toggleMobileMenu(); }}
                    selected={isActive(item.path)}
                    sx={{
                      borderRadius: 2,
                      pl: 2,
                      pr: 3,
                      py: 1,
                      '&.Mui-selected': {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.18),
                        },
                        '& .MuiListItemIcon-root': {
                          color: theme.palette.primary.main,
                        },
                        '& .MuiListItemText-primary': {
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                    <ListItemText 
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: isActive(item.path) ? 600 : 400
                      }}
                    />
                    {isActive(item.path) && (
                      <Box 
                        sx={{ 
                          width: 4, 
                          height: 24, 
                          bgcolor: 'primary.main', 
                          borderRadius: 1,
                          ml: 1
                        }} 
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            
            <Divider sx={{ my: 1, opacity: 0.6 }} />
            
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography 
                variant="overline" 
                color="text.secondary" 
                fontWeight={700}
                sx={{ letterSpacing: 1 }}
              >
                SYSTEM
              </Typography>
            </Box>
            
            <List sx={{ px: 1.5 }}>
              {/* <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton 
                  onClick={() => { navigate("/security"); toggleMobileMenu(); }}
                  sx={{ 
                    borderRadius: 2,
                    pl: 2,
                    pr: 3,
                    py: 1
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}><Security fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Security" />
                </ListItemButton>
              </ListItem> */}
              
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton 
                  onClick={() => { navigate("/admin-management"); toggleMobileMenu(); }}
                  sx={{ 
                    borderRadius: 2,
                    pl: 2,
                    pr: 3,
                    py: 1
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}><SupervisorAccount fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Admin Users" />
                </ListItemButton>
              </ListItem>
            </List>
            
            <DrawerFooter>
              <Button 
                variant="outlined" 
                fullWidth
                color="error"
                onClick={handleLogout}
                startIcon={<ExitToApp />}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  py: 1
                }}
              >
                Logout
              </Button>
            </DrawerFooter>
          </Drawer>

          {/* Help Menu */}
          <Menu
            anchorEl={helpAnchorEl}
            open={Boolean(helpAnchorEl)}
            onClose={handleHelpClose}
            TransitionComponent={Fade}
            PaperProps={{
              component: StyledPaper,
              elevation: 3,
              sx: {
                minWidth: 200,
                mt: 1.5,
                overflow: 'visible',
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                }
              }
            }}
          >
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Help & Resources
              </Typography>
            </Box>
            <MenuItemStyled onClick={() => { navigate('/help-center'); handleHelpClose(); }}>
              <ListItemText primary="Help Center" />
            </MenuItemStyled>
            <MenuItemStyled onClick={() => { navigate('/documentation'); handleHelpClose(); }}>
              <ListItemText primary="Documentation" />
            </MenuItemStyled>
            <MenuItemStyled onClick={() => { navigate('/contact-support'); handleHelpClose(); }}>
              <ListItemText primary="Contact Support" />
            </MenuItemStyled>
            <Divider sx={{ my: 1, mx: 2 }} />
            <MenuItemStyled onClick={() => { window.open('https://example.com/tutorials', '_blank'); handleHelpClose(); }}>
              <ListItemText primary="Video Tutorials" />
              <ChevronRight fontSize="small" color="action" />
            </MenuItemStyled>
          </Menu>

          {/* Notifications Menu */}
          <Menu
            anchorEl={notificationAnchorEl}
            open={Boolean(notificationAnchorEl)}
            onClose={handleNotificationClose}
            TransitionComponent={Fade}
            PaperProps={{
              component: StyledPaper,
              elevation: 3,
              sx: {
                width: { xs: 300, sm: 360 },
                maxHeight: '70vh',
                mt: 1.5,
                overflow: 'visible',
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                }
              }
            }}
          >
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Notifications
              </Typography>
              <Button 
                variant="text" 
                size="small" 
                sx={{ 
                  textTransform: 'none', 
                  fontWeight: 500,
                  minWidth: 0,
                  p: 0.5
                }}
                onClick={() => {
                  setNotifications(notifications.map(n => ({ ...n, read: true })));
                  setUnreadCount(0);
                  handleNotificationClose();
                }}
              >
                Mark all as read
              </Button>
            </Box>
            <Divider />
            {notifications.length > 0 ? (
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {notifications.map((notification) => (
                  <MenuItemStyled 
                    key={notification.id}
                    onClick={() => {
                      // Mark as read
                      setNotifications(notifications.map(n => 
                        n.id === notification.id ? { ...n, read: true } : n
                      ));
                      setUnreadCount(prev => prev - (!notification.read ? 1 : 0));
                      handleNotificationClose();
                    }}
                    sx={{ 
                      borderLeft: notification.read ? 'none' : `4px solid ${theme.palette.primary.main}`,
                      bgcolor: notification.read ? 'transparent' : alpha(theme.palette.primary.light, 0.07),
                    }}
                  >
                    <ListItemIcon>
                      {getNotificationIcon(notification.type)}
                    </ListItemIcon>
                    <ListItemText 
                      primary={notification.message}
                      secondary={notification.time}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: notification.read ? 400 : 600,
                        color: theme.palette.text.primary,
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        color: theme.palette.text.secondary,
                      }}
                    />
                  </MenuItemStyled>
                ))}
              </Box>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <NotificationsNone 
                  sx={{ 
                    fontSize: 48, 
                    color: theme.palette.action.disabled,
                    mb: 1
                  }} 
                />
                <Typography variant="body2" color="text.secondary">
                  No new notifications
                </Typography>
              </Box>
            )}
            <Divider />
            <Box sx={{ p: 1.5 }}>
              <Button 
                fullWidth
                variant="outlined"
                onClick={() => {
                  navigate('/notifications');
                  handleNotificationClose();
                }}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  py: 1
                }}
              >
                View All Notifications
              </Button>
            </Box>
          </Menu>

          {/* Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            TransitionComponent={Fade}
            PaperProps={{
              component: StyledPaper,
              elevation: 3,
              sx: {
                width: 280,
                mt: 1.5,
                overflow: 'visible',
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                }
              }
            }}
          >
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <UserAvatar sx={{ width: 70, height: 70, mx: 'auto', mb: 2 }}>
                {adminInitial}
              </UserAvatar>
              <Typography variant="subtitle1" fontWeight={600}>
                {currentUser}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {admin?.email || "admin@example.com"}
              </Typography>
              <Chip 
                label="Administrator" 
                size="small" 
                color="primary" 
                sx={{ 
                  mt: 1, 
                  fontWeight: 500,
                  color: 'white'
                }} 
              />
            </Box>
            <Divider />
            {/* <MenuItemStyled onClick={() => { navigate("/profile"); handleMenuClose(); }}>
              <ListItemIcon><Person fontSize="small" /></ListItemIcon>
              <ListItemText primary="My Profile" />
            </MenuItemStyled> */}
            <MenuItemStyled onClick={() => { navigate("/admin-management"); handleMenuClose(); }}>
              <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
              <ListItemText primary="Account Settings" />
            </MenuItemStyled>
            <Divider sx={{ my: 1, mx: 2 }} />
            <MenuItemStyled 
              onClick={handleLogout}
              sx={{ color: theme.palette.error.main }}
            >
              <ListItemIcon sx={{ color: theme.palette.error.main }}>
                <ExitToApp fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItemStyled>
          </Menu>
        </Toolbar>
      </Container>
    </StyledAppBar>
  );
};

export default Navbar;